import { useCallback, useMemo } from "react";
import {
  useAnalyticsContext,
  useUserContext,
  hasMinProfile,
} from "@WahooFitness/wahoo-offline-mfe";
import { Profile } from "@WahooFitness/cloud-client-types";
import { FlaggedFeature } from "./types/FlaggedFeature";
import { useSettingsRemoteConfig } from "./useSettingsRemoteConfig";
import { SettingsRemoteConfigKey } from "@/services/SettingsDefaultRemoteConfig";

const useFlaggedFeatures = () => {
  const { currentUser, userIsLoading } = useUserContext();
  const { remoteConfigIsLoading } = useAnalyticsContext();
  const { getSettingsRemoteConfigString, getSettingsRemoteConfigObject } =
    useSettingsRemoteConfig();

  const checkMinProfileFeature = useCallback(
    (remoteConfigKey: SettingsRemoteConfigKey) =>
      hasMinProfile(
        getSettingsRemoteConfigString(remoteConfigKey) as Profile,
        currentUser?.app?.profile
      ),
    [currentUser?.app?.profile, getSettingsRemoteConfigString]
  );

  const featureCheckers = useMemo<Record<FlaggedFeature, () => boolean>>(() => {
    return {
      [FlaggedFeature.ElemntMultisportPage]: () =>
        checkMinProfileFeature("ELEMNT_MULTISPORT_PAGE_MIN_PROFILE"),
      [FlaggedFeature.ElemntPlannedWorkouts]: () =>
        checkMinProfileFeature("ELEMNT_PLANNED_WORKOUTS_MIN_PROFILE"),
    };
  }, [checkMinProfileFeature]);

  const curatedWorkoutsList = useMemo(
    () => getSettingsRemoteConfigObject<number[]>("CURATED_WORKOUTS_LIST"),
    [getSettingsRemoteConfigObject]
  );

  const checkIsFeatureEnabled = useCallback(
    (feature: FlaggedFeature) => featureCheckers[feature](),
    [featureCheckers]
  );

  const featuresAreLoading = useMemo(
    () => remoteConfigIsLoading || userIsLoading,
    [remoteConfigIsLoading, userIsLoading]
  );

  const fitnessTestId = useMemo(
    () => getSettingsRemoteConfigString("FITNESS_TEST_ID"),
    [getSettingsRemoteConfigString]
  );

  return {
    curatedWorkoutsList: curatedWorkoutsList || [],
    checkIsFeatureEnabled,
    featuresAreLoading,
    fitnessTestId,
  };
};
export default useFlaggedFeatures;
